import { useEffect, useState } from "react";
import { Download, ExternalLink, FileText, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "@/lib/api";
import { downloadBlob, fetchOnePageBlob, shareOnePage } from "@/lib/onePages";

const Body = ({ pg, url, onImgError, imgFailed }) => {
  if (!url) return <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  if (pg.kind === "pdf") return <iframe data-testid="onepage-preview-pdf" title={pg.original_filename} src={url} className="w-full h-[65vh] rounded-lg bg-white" />;
  if (pg.kind === "image" && !imgFailed) return <img data-testid="onepage-preview-image" src={url} alt={pg.original_filename} onError={onImgError} className="w-full max-h-[65vh] object-contain rounded-lg" />;
  return (
    <div data-testid="onepage-preview-unavailable" className="h-48 flex flex-col items-center justify-center gap-2 text-center">
      <FileText className="w-10 h-10 c-accent" />
      <p className="text-sm c-text-2">Vista previa no disponible para este formato.<br />Descárgalo o ábrelo con otra app.</p>
    </div>
  );
};

export const OnePageViewer = ({ pg, open, onOpenChange }) => {
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    if (!open || !pg) return undefined;
    let u;
    setUrl(null); setBlob(null); setImgFailed(false);
    fetchOnePageBlob(pg).then((b) => { u = URL.createObjectURL(b); setBlob(b); setUrl(u); }).catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
    return () => u && URL.revokeObjectURL(u);
  }, [open, pg, onOpenChange]);
  if (!pg) return null;
  const share = async () => { try { if ((await shareOnePage(pg, blob)) === "downloaded") toast.success("Descargado"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="onepage-viewer" className="w-[96vw] max-w-lg p-3 gap-3 rounded-2xl">
        <DialogTitle className="text-sm font-semibold pr-8 truncate">{pg.original_filename}</DialogTitle>
        <DialogDescription className="sr-only">Vista previa del one page</DialogDescription>
        <Body pg={pg} url={url} imgFailed={imgFailed} onImgError={() => setImgFailed(true)} />
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="onepage-viewer-share-btn" disabled={!blob} onClick={share} className="py-2.5 rounded-xl c-btn-primary text-sm flex items-center justify-center gap-1.5"><Share2 className="w-4 h-4" /> Compartir</button>
          <button data-testid="onepage-viewer-download-btn" disabled={!blob} onClick={() => downloadBlob(blob, pg.original_filename)} className="py-2.5 rounded-xl c-card text-sm font-semibold flex items-center justify-center gap-1.5"><Download className="w-4 h-4" /> Descargar</button>
          <button data-testid="onepage-viewer-open-btn" disabled={!url} onClick={() => window.open(url, "_blank")} className="py-2.5 rounded-xl c-card text-sm font-semibold flex items-center justify-center gap-1.5"><ExternalLink className="w-4 h-4" /> Abrir</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
